import React, { memo, useContext, useState } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import type { UseAnalyticsEventsHook } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap } from '@atlaskit/css';

import {
	RESTRICTIONS_DIALOG_EXPERIENCE,
	RESTRICTIONS_DIALOG_LOAD_EXPERIENCE,
	RESTRICTIONS_DIALOG_APPLY_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import {
	ChangeEditionInlineDialogLoader,
	ChangeEditionInlineDialogPaintLoader,
} from '@confluence/change-edition/entry-points/InlineDialog.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { GrantAccessFlagLoader } from '@confluence/grant-access-dialog/entry-points/GrantAccessFlagLoader.tsx';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { useContentId } from '@confluence/page-restrictions-context/entry-points/useContentId.ts';
import { PageRestrictionsContextConsumer } from '@confluence/page-restrictions-context/entry-points/PageRestrictionsContext.ts';
import type { RestrictionsType } from '@confluence/page-restrictions-context/entry-points/PageRestrictionsContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useContentType } from '@confluence/page-context';
import { RestrictionsDialogStateContainer } from '@confluence/restrictions-dialog-state-container';

import { RESTRICTIONS_BUTTON_METRIC, RESTRICTIONS_DIALOG_METRIC } from '../perf.config.ts';
import {
	RestrictionsButtonLoaderAfterPaint,
	RestrictionsButtonLoaderPaint,
} from '../RestrictionsButton/index.tsx';
import { RestrictionsButtonPlaceholder } from '../RestrictionsButton/RestrictionsButtonPlaceholder.tsx';
import { RestrictionsButtonLoading } from '../RestrictionsButton/RestrictionsButtonLoading.tsx';
import type { RestrictionsDialogTriggerProps } from '../types.ts';

const styles = cssMap({
	buttonContainer: {
		display: 'inline-block',
		verticalAlign: 'top',
	},
});

const INLINE_DIALOG_PLACEMENT = 'bottom-end';

export const RestrictionsDialogTrigger = memo(
	(props: RestrictionsDialogTriggerProps): React.JSX.Element => {
		const contentId = useContentId(props.contentId) || '';
		const { edition } = useSessionData();
		const [state, setState] = useState({
			grantAccessClicked: false,
			hasAutoOpenedRestrictionDialog: false,
			showFreeRestrictionDialog: false,
		});
		const contentSubType = props.contentSubType;

		const useAnalyticsEventsHook = useAnalyticsEvents();
		const [contentType] = useContentType();
		const experienceTracker = useContext(ExperienceTrackerContext);

		const makeHandleOpen = (dialogStateContainer: RestrictionsDialogStateContainer) => () =>
			startRestrictionsDialogExperience({
				...useAnalyticsEventsHook,
				actionSubjectId: 'restrictionsDialogButton',
				dialogStateContainer,
				edition,
				experienceTracker,
				contentType,
				contentSubType,
				contentId,
			});

		const getParentData = (restrictions?: RestrictionsType) => {
			const ancestors = restrictions?.ancestors || [];
			for (let i = ancestors.length - 1; i >= 0; i--) {
				if (ancestors[i].hasRestrictions) {
					return ancestors[i]; // content
				}
			}
		};

		const handleFreeEditionPadlockClicked = () => {
			setState((state) => ({ ...state, showFreeRestrictionDialog: true }));
		};

		const onFreeDialogClosed = () => {
			setState((state) => ({ ...state, showFreeRestrictionDialog: false }));
		};

		const renderFreeEditionDialog = (
			dialogStateContainer: RestrictionsDialogStateContainer,
			shouldShowDialog: boolean,
		) => {
			const { isDisabled, triggeredFrom } = props;

			// already free edition
			const shouldSSRViewPageFreeEditionButton = triggeredFrom === 'fromView';
			return (
				<PageRestrictionsContextConsumer>
					{({ loading, error, restrictions }) => {
						if ((!restrictions && loading) || error) {
							return <RestrictionsButtonPlaceholder />;
						}

						const contentHasRestrictions = Boolean(
							restrictions?.hasRestrictions || restrictions?.hasInheritedRestrictions,
						);
						const contentHasInheritedRestrictionsOnly = Boolean(
							restrictions?.hasInheritedRestrictions && !restrictions?.hasRestrictions,
						);

						const immediateParentPageIndex = (restrictions?.ancestors.length || 0) - 1;
						const isChildPage = immediateParentPageIndex > 0;

						// Note: content with restrictions is excluded from the pageRestrictionsExperiment
						if (contentHasRestrictions) {
							if (isChildPage && contentHasInheritedRestrictionsOnly) {
								const parentData = getParentData(restrictions);

								return shouldSSRViewPageFreeEditionButton ? (
									<ChangeEditionInlineDialogPaintLoader
										isOpen={state.showFreeRestrictionDialog}
										onClose={onFreeDialogClosed}
										parentData={parentData}
										isChildPage={isChildPage}
										placement={INLINE_DIALOG_PLACEMENT}
									>
										<RestrictionsButtonLoaderPaint
											contentId={contentId}
											isDisabled={isDisabled}
											onClick={handleFreeEditionPadlockClicked}
											restrictionsButtonSSRRendered
											restrictionsButtonSSRLocation="free inherited"
											triggeredFrom={triggeredFrom}
										/>
									</ChangeEditionInlineDialogPaintLoader>
								) : (
									<ChangeEditionInlineDialogLoader
										isOpen={state.showFreeRestrictionDialog}
										onClose={onFreeDialogClosed}
										parentData={parentData}
										isChildPage={isChildPage}
										placement={INLINE_DIALOG_PLACEMENT}
									>
										<RestrictionsButtonLoaderAfterPaint
											contentId={contentId}
											isDisabled={isDisabled}
											onClick={handleFreeEditionPadlockClicked}
											restrictionsButtonSSRLocation="free inherited"
											triggeredFrom={triggeredFrom}
										/>
									</ChangeEditionInlineDialogLoader>
								);
							}

							if (shouldShowDialog && !state.hasAutoOpenedRestrictionDialog) {
								makeHandleOpen(dialogStateContainer)();
								setState((state) => ({
									...state,
									hasAutoOpenedRestrictionDialog: true,
								}));
							}
							// grandfathered content
							return shouldSSRViewPageFreeEditionButton ? (
								<RestrictionsButtonLoaderPaint
									contentId={contentId}
									isDisabled={isDisabled}
									resetDialogState={() => dialogStateContainer.initState()}
									onClick={makeHandleOpen(dialogStateContainer)}
									restrictionsButtonSSRRendered
									restrictionsButtonSSRLocation="free grandfather"
									triggeredFrom={triggeredFrom}
								/>
							) : (
								<RestrictionsButtonLoaderAfterPaint
									contentId={contentId}
									isDisabled={isDisabled}
									onClick={makeHandleOpen(dialogStateContainer)}
									restrictionsButtonSSRLocation="free grandfather"
									triggeredFrom={triggeredFrom}
								/>
							);
						}
						return shouldSSRViewPageFreeEditionButton ? (
							<ChangeEditionInlineDialogPaintLoader
								isOpen={state.showFreeRestrictionDialog}
								onClose={onFreeDialogClosed}
								placement={INLINE_DIALOG_PLACEMENT}
							>
								<RestrictionsButtonLoaderPaint
									contentId={contentId}
									isDisabled={isDisabled}
									onClick={handleFreeEditionPadlockClicked}
									restrictionsButtonSSRRendered
									restrictionsButtonSSRLocation="free"
									triggeredFrom={triggeredFrom}
								/>
							</ChangeEditionInlineDialogPaintLoader>
						) : (
							<ChangeEditionInlineDialogLoader
								isOpen={state.showFreeRestrictionDialog}
								onClose={onFreeDialogClosed}
								placement={INLINE_DIALOG_PLACEMENT}
							>
								<RestrictionsButtonLoaderAfterPaint
									contentId={contentId}
									isDisabled={isDisabled}
									onClick={handleFreeEditionPadlockClicked}
									restrictionsButtonSSRLocation="free"
									triggeredFrom={triggeredFrom}
								/>
							</ChangeEditionInlineDialogLoader>
						);
					}}
				</PageRestrictionsContextConsumer>
			);
		};

		const renderRestrictionsButton = (dialogStateContainer: RestrictionsDialogStateContainer) => {
			const { isDisabled, triggeredFrom } = props;

			if (triggeredFrom && triggeredFrom === 'fromView') {
				return (
					<RestrictionsButtonLoaderPaint
						contentId={contentId}
						isDisabled={isDisabled}
						onClick={makeHandleOpen(dialogStateContainer)}
						resetDialogState={() => dialogStateContainer.initState()}
						restrictionsButtonSSRRendered
						restrictionsButtonSSRLocation="default"
						triggeredFrom={triggeredFrom}
					/>
				);
			}
			return (
				<RestrictionsButtonLoaderAfterPaint
					contentId={contentId}
					isDisabled={isDisabled}
					onClick={makeHandleOpen(dialogStateContainer)}
					restrictionsButtonSSRLocation="default"
					triggeredFrom={triggeredFrom}
				/>
			);
		};

		const renderGrantAccessFlagAndButton = (
			dialogStateContainer: RestrictionsDialogStateContainer,
		) => (
			<>
				{renderRestrictionsButton(dialogStateContainer)}
				<GrantAccessFlagLoader contentId={contentId} />
			</>
		);

		return (
			<Subscribe to={[RestrictionsDialogStateContainer]}>
				{(dialogStateContainer: RestrictionsDialogStateContainer) => (
					<Box xcss={styles.buttonContainer}>
						<PageSegmentLoadStart
							isCustomStart
							key={`start-${contentId}`}
							metric={RESTRICTIONS_BUTTON_METRIC}
						/>

						{edition ? (
							<RoutesContext.Consumer>
								{({ getQueryParams }) => {
									const {
										accountId,
										grantAccess,
										// TODO(brizvash): non-gdpr params, remove when GDPR is on
										username,
									} = getQueryParams();

									const shouldShowGrantAccessDialog = Boolean(
										(grantAccess && accountId) || username,
									);

									return edition === ConfluenceEdition.FREE
										? renderFreeEditionDialog(dialogStateContainer, shouldShowGrantAccessDialog)
										: renderGrantAccessFlagAndButton(dialogStateContainer);
								}}
							</RoutesContext.Consumer>
						) : (
							<RestrictionsButtonLoading />
						)}
					</Box>
				)}
			</Subscribe>
		);
	},
);

RestrictionsDialogTrigger.displayName = 'RestrictionsDialogTrigger';

export function startRestrictionsDialogExperience({
	actionSubjectId,
	createAnalyticsEvent,
	dialogStateContainer,
	edition,
	experienceTracker,
	triggeredFrom,
	contentType,
	contentSubType,
	contentId,
}: {
	actionSubjectId: string;
	dialogStateContainer: RestrictionsDialogStateContainer;
	edition: ConfluenceEdition | null;
	experienceTracker: ExperienceTrackerAPI;
	triggeredFrom?: string;
	contentType?: string;
	contentSubType?: string | null;
	contentId: string;
} & UseAnalyticsEventsHook): void {
	createAnalyticsEvent({
		type: 'sendUIEvent',
		data: {
			source: 'viewPageScreen',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId,
			attributes: {
				source: 'v2',
				triggeredFrom,
				edition,
				contentType,
				isLivePage: contentSubType === 'live',
			},
		},
	}).fire();
	experienceTracker.start({
		name: RESTRICTIONS_DIALOG_EXPERIENCE,
		attributes: {
			contentType,
		},
		collect(events, experience) {
			// Fail/Abort RESTRICTIONS_DIALOG as soon as _LOAD or _APPLY fails or aborts:
			experience.stopOn(
				events.find(
					({ action, name }) =>
						(action === 'taskFail' || action === 'taskAbort') &&
						(name === RESTRICTIONS_DIALOG_LOAD_EXPERIENCE ||
							name === RESTRICTIONS_DIALOG_APPLY_EXPERIENCE),
				),
			);
		},
	});
	experienceTracker.start({
		name: RESTRICTIONS_DIALOG_LOAD_EXPERIENCE,
		timeout: ExperienceTimeout.DEFAULT,
	});

	//
	// Open RestrictionsDialog:
	//

	void dialogStateContainer.setContentId(contentId);
	void dialogStateContainer.setIsOpen(true, triggeredFrom);

	RESTRICTIONS_DIALOG_METRIC.start();
}
